import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  buildCanonical,
  pageTitle,
} from "@/lib/seo";

interface SEOProps {
  /** Page title — appended with " | Vello Play" automatically when needed. */
  title?: string;
  description?: string;
  /** Canonical path (e.g. "/funcionalidades"). Defaults to current pathname. */
  canonicalPath?: string;
  /** Set to true for private/auth/utility pages. */
  noIndex?: boolean;
  /** Open Graph image URL (absolute or relative). */
  image?: string;
  /** og:type — "website" | "article" | "profile" | etc. */
  ogType?: string;
  /** Optional structured data. Pass a single object or an array. */
  jsonLd?: object | object[];
  /** Extra keywords (comma separated). */
  keywords?: string;
}

/**
 * Centralized SEO component for Vello Play.
 * Applies title, description, canonical, robots, Open Graph, Twitter, and JSON-LD.
 */
export const SEO = ({
  title,
  description,
  canonicalPath,
  noIndex = false,
  image,
  ogType = "website",
  jsonLd,
  keywords,
}: SEOProps) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = buildCanonical(path);
  const fullTitle = pageTitle(title);
  const fullDescription =
    description ??
    "Vello Play é a plataforma de gestão esportiva para torneios e arenas: reservas, inscrições online, chaveamento automático, financeiro e portal do atleta.";
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image.startsWith("/") ? image : `/${image}`}`
    : DEFAULT_OG_IMAGE;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="pt-BR" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VelloPlay" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
